// Shared pieces: the page frame with its side menu, links that don't reload the
// page, loading data, and small building blocks.

import { createContext, useContext, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { api, message } from './api.ts';
import type { Person } from './types.ts';

// ---- Moving between pages without reloading ----

export const Route = createContext<{ path: string; go: (to: string) => void }>({ path: '/', go: () => {} });
export const useRoute = () => useContext(Route);

export function Link({ to, className, children, title, style }: { to: string; className?: string; children: ReactNode; title?: string; style?: CSSProperties }) {
  const { go } = useRoute();
  return (
    <a
      href={to}
      className={className}
      title={title}
      style={style}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        go(to);
      }}
    >
      {children}
    </a>
  );
}

// ---- Who's signed in ----

export const Me = createContext<{ person: Person | null; setPerson: (p: Person | null) => void }>({ person: null, setPerson: () => {} });
export const useMe = () => useContext(Me);

// ---- Loading data ----

export function useLoad<T>(load: () => Promise<T>, deps: unknown[]) {
  const [state, setState] = useState<{ data?: T; error?: string; loading: boolean }>({ loading: true });
  const [round, setRound] = useState(0);
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    load().then(
      (data) => live && setState({ data, loading: false }),
      (e) => live && setState({ error: message(e), loading: false }),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, round]);
  return { ...state, reload: () => setRound((r) => r + 1) };
}

/**
 * Loads again every few seconds while something is happening elsewhere, e.g. a change
 * waiting for approval on the ops tablet, so the page shows the answer as soon as it comes.
 */
export function useRefreshWhile(active: boolean, reload: () => void, everyMs = 4000) {
  useEffect(() => {
    if (!active) return;
    const t = setInterval(reload, everyMs);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, everyMs]);
}

// ---- Saving what's typed ----

/**
 * Saves typing without waiting for the box to lose focus. Call `edited()` after each
 * change: `save` runs once typing pauses (or straight away with `flush()`, e.g. when the
 * box loses focus). While anything typed isn't saved yet, or a save is still on its way,
 * leaving or reloading the page asks first, so the last words can't be lost quietly.
 */
export function useSaveWhenTypingStops(save: () => Promise<unknown> | void, delay = 800) {
  const [edits, setEdits] = useState(0);
  const saved = useRef(0);
  const latest = useRef(save);
  latest.current = save;
  const unsaved = useRef(false);
  unsaved.current = edits !== saved.current;
  const inFlight = useRef(0);

  const run = (upTo: number) => {
    saved.current = upTo;
    unsaved.current = false;
    const done = latest.current();
    if (done instanceof Promise) {
      inFlight.current++;
      void done.finally(() => inFlight.current--);
    }
  };
  useEffect(() => {
    if (edits === saved.current) return;
    const t = setTimeout(() => run(edits), delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edits, delay]);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (unsaved.current || inFlight.current > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  return {
    edited: () => setEdits((n) => n + 1),
    flush: () => {
      if (edits !== saved.current) run(edits);
    },
  };
}

// ---- Icons (inline, so nothing loads from elsewhere) ----

const icon = (d: ReactNode) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);
export const Icons = {
  sites: icon(
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>,
  ),
  plus: icon(
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>,
  ),
  team: icon(
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19c.8-3.2 3-5 5.5-5s4.7 1.8 5.5 5" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M16 14.2c2.3.2 3.9 1.8 4.5 4.8" />
    </>,
  ),
};

// ---- The page frame ----

export function Shell({ children }: { children: ReactNode }) {
  const { path, go } = useRoute();
  const { person, setPerson } = useMe();
  const signOut = async () => {
    try {
      await api('POST', '/auth/sign-out', {});
    } finally {
      setPerson(null);
      go('/');
    }
  };
  const nav = (to: string, label: string, ic: ReactNode, on: boolean) => (
    <Link to={to} className={on ? 'navlink on' : 'navlink'}>
      {ic}
      {label}
    </Link>
  );
  return (
    <div className="shell">
      <nav className="side" aria-label="Console">
        <div className="brand">
          <span className="brand-mark">Z.A.S.</span>
          <span className="faint">Console</span>
        </div>
        {nav('/sites', 'Sites', Icons.sites, path === '/sites' || (path.startsWith('/sites/') && !path.endsWith('/new')))}
        {nav('/new', 'New site', Icons.plus, path === '/new')}
        {nav('/team', 'Team', Icons.team, path === '/team')}
        <div className="side-foot">
          <span>{person?.name}</span>
          <span className="faint small">Z.A.S. support</span>
          <button className="linkish" style={{ marginTop: 8 }} onClick={signOut}>
            Sign out
          </button>
        </div>
      </nav>
      <main className="page">{children}</main>
    </div>
  );
}

export function Chip({ tone, children }: { tone: 'green' | 'amber' | 'red' | 'plain'; children: ReactNode }) {
  return <span className={`chip ${tone}`}>{children}</span>;
}

export function Loading() {
  return <p className="faint">Loading…</p>;
}

export function Problem({ text }: { text: string }) {
  return (
    <div className="note red" role="alert">
      {text}
    </div>
  );
}

export function Dialog({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-label={title}>
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}
